import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import type { api } from '@stageplottter/core';
import { getApi } from '../api/endpoints';
import { isApiError } from '../api/client';
import { settingsStore } from '../state/settings';

export type MeState =
  | { status: 'loading' }
  | { status: 'ready'; me: api.Me }
  | { status: 'offline' }
  | { status: 'anonymous' };

/** Retry delays while /me is unreachable (server restarting, network gone): 5 s doubling to 60 s. */
export const ME_RETRY_MS = { first: 5_000, max: 60_000 };

let retry: ReturnType<typeof setTimeout> | undefined;
let delay = ME_RETRY_MS.first;
let onOnline: (() => void) | undefined;

function stopRetry(): void {
  if (retry !== undefined) clearTimeout(retry);
  retry = undefined;
  if (onOnline) globalThis.removeEventListener?.('online', onOnline);
  onOnline = undefined;
}

export const meStore = createStore<{
  state: MeState;
  load(opts?: { redirectOn401?: boolean }): Promise<void>;
}>()((set, get) => ({
  state: { status: 'loading' },
  async load(opts = {}) {
    stopRetry();
    try {
      set({ state: { status: 'ready', me: await getApi().me(opts) } });
      delay = ME_RETRY_MS.first;
    } catch (err) {
      if (isApiError(err, 401)) {
        set({ state: { status: 'anonymous' } });
        return;
      }
      // offline, or the server answered with an error: try again later (and once back online)
      set({ state: { status: 'offline' } });
      const again = () => void get().load(opts);
      retry = setTimeout(again, delay);
      delay = Math.min(delay * 2, ME_RETRY_MS.max);
      onOnline = again;
      globalThis.addEventListener?.('online', onOnline);
    }
  },
}));

export function useMe(): MeState {
  return useStore(meStore, (s) => s.state);
}

/**
 * The account follows the app language (U87): e-mails (invitations, digests) are written in the
 * language the member uses here – sent once after sign-in and on every change.
 */
function syncLocale(): void {
  const st = meStore.getState().state;
  if (st.status !== 'ready') return;
  const lang = settingsStore.getState().language;
  if (st.me.locale === lang) return;
  meStore.setState({ state: { status: 'ready', me: { ...st.me, locale: lang } } });
  // never in the way of loading /me
  void Promise.resolve()
    .then(() => getApi().updateMe({ locale: lang }))
    .catch(() => undefined);
}
meStore.subscribe((s, prev) => {
  if (s.state.status === 'ready' && prev.state.status !== 'ready') syncLocale();
});
settingsStore.subscribe((s, prev) => {
  if (s.language !== prev.language) syncLocale();
});
